import { Settings, Building2, MessageCircle, Link2, MapPin } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useConnections } from '@/hooks/useConnections'
import type { WizardData } from './types'

interface StepReviewProps {
  data: WizardData
}

function Section({
  title,
  icon,
  empty,
  children,
}: {
  title: string
  icon: React.ReactNode
  empty?: boolean
  children?: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <span className="text-sm font-semibold">{title}</span>
        </div>
        {empty ? (
          <span className="text-sm text-muted-foreground ml-6">— Not configured</span>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-muted last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  )
}

const providerLabels: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google Gemini',
}

export function StepReview({ data }: StepReviewProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Review Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Verify everything looks correct before saving.
        </p>
      </div>

      <Section title="Core Settings" icon={<Settings className="h-4 w-4 text-muted-foreground" />}>
        <Row label="Name" value={data.configName || '—'} />
        <Row
          label="Provider / Model"
          value={`${providerLabels[data.provider] || data.provider} / ${data.model || '—'}`}
        />
        <Row label="Session TTL" value={`${data.sessionTTL}s`} />
        <Row label="SRO Enabled" value={data.enableSRO ? 'Yes' : 'No'} />
        <Row
          label="Skip Address Validation"
          value={data.serviceArea.skip_address_validation ? 'Yes' : 'No'}
        />
      </Section>

      <Section
        title={`Departments (${data.departments.length})`}
        icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
        empty={data.departments.length === 0}
      >
        {data.departments.map((d, i) => (
          <div
            key={i}
            className="py-1.5 border-b border-muted last:border-0 flex items-center gap-2"
          >
            <span className="text-sm font-medium">{d.name}</span>
            <Badge variant="outline" className="font-mono text-xs">
              {d.code}
            </Badge>
            {d.sub_departments.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({d.sub_departments.map((s) => s.name).join(', ')})
              </span>
            )}
          </div>
        ))}
      </Section>

      <Section
        title={`Intents (${data.intents.length})`}
        icon={<MessageCircle className="h-4 w-4 text-muted-foreground" />}
        empty={data.intents.length === 0}
      >
        {data.intents.map((intent, i) => (
          <div
            key={i}
            className="py-1.5 border-b border-muted last:border-0 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{intent.name}</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{intent.workflow}</span>
            </div>
            {intent.is_default && <Badge variant="secondary" className="text-xs">Default</Badge>}
          </div>
        ))}
      </Section>

      <ConnectionReviewSection connectionId={data.app_connection_id} />

      <Section
        title="Service Area"
        icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
        empty={!data.serviceArea.enabled}
      >
        {data.serviceArea.enabled && (
          <>
            <Row label="City" value={data.serviceArea.city_name || '—'} />
            <Row
              label="Center"
              value={`${data.serviceArea.center_lat || '—'}, ${data.serviceArea.center_lng || '—'}`}
            />
            {data.serviceArea.allowed_zip_codes && (
              <Row label="ZIP Codes" value={data.serviceArea.allowed_zip_codes} />
            )}
          </>
        )}
      </Section>
    </div>
  )
}

function ConnectionReviewSection({ connectionId }: { connectionId: number | null }) {
  const { data: connections } = useConnections()
  const connection = connectionId != null ? connections?.find((c) => c.id === connectionId) : null

  return (
    <Section
      title="App Connection"
      icon={<Link2 className="h-4 w-4 text-muted-foreground" />}
      empty={!connection}
    >
      {connection && (
        <>
          <Row label="Provider" value={connection.provider.charAt(0).toUpperCase() + connection.provider.slice(1)} />
          <Row label="Label" value={connection.label} />
          <Row label="Status" value={connection.status} />
        </>
      )}
    </Section>
  )
}
